'use client'

import { Pencil24, Plus24 } from '@vinted/monochrome-icons'
import {
  Button,
  Card,
  Cell,
  Checkbox,
  Divider,
  Icon,
  InputText,
  Loader,
  Spacer,
  Text,
  Validation,
} from '@vinted/web-ui'
import { camelCase } from 'lodash'
import { ReactNode, useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'

import { ErrorItem, ResponseError } from '@marketplace-web/shared/api-client'
import { useLocation } from '@marketplace-web/shared/browser'
import { useTracking } from '@marketplace-web/shared/event-tracker'
import { useFeatureSwitch } from '@marketplace-web/shared/feature-switches'
import { useTranslate } from '@marketplace-web/shared/i18n'
import { useSession } from '@marketplace-web/shared/session'

import {
  renderValidation,
  SeparatedList,
  useFormValidationMessage,
} from '@marketplace-web/shared/ui-helpers'
import { isValueInObject, urlWithParams } from '@marketplace-web/shared/utils'

import { clickEvent, viewScreenEvent } from '_libs/common/event-tracker/events'
import { formatCurrency } from '_libs/utils/formatString'
import {
  BUSINESS_TERMS_AND_CONDITIONS_URL,
  BUSINESS_TERMS_OF_USE_URL,
  PRIVATE_BETA_TERMS_OF_USE_URL,
  WALLET_ACCOUNT_EDIT_URL,
} from 'constants/routes'
import { ClickableElement } from 'constants/tracking/clickable-elements'
import { Screen } from 'constants/tracking/screens'
import { TwoFactorAuthentificationType } from 'constants/two-factor-authentification'
import { convertBusinessWallet } from 'data/api/convert-business-wallet'
import { getBankAccounts } from 'data/api/get-bank-accounts'
import { getWalletBalance } from 'data/api/get-wallet-balance'
import { updateBusinessAccountBankDetails } from 'data/api/update-business-account-bank-details'
import { HttpStatus, ResponseCode } from 'data/api/response-codes'
import { transformBankAccounts } from 'data/transformers/bank-account'
import { transformWalletConversionError } from 'data/transformers/business-account'
import { transformWalletBalanceDto } from 'data/transformers/wallet'
import { WalletConversionErrorDto } from 'types/dtos/business-account'
import { BankAccountModel } from 'types/models/bank-account'
import { WalletBalanceModel } from 'types/models/wallet-balance'
import { WalletConversionErrorModel } from 'types/models/business-account'

import { CountryId, WalletConversionStatus } from '../../../constants'
import { GeneralError } from '../../../components/Register/GeneralError'
import ErrorModal from './ErrorModal'
import useFetchBusinessAddresses from './hooks/useFetchBusinessAddresses'

type FormData = {
  bankAccountOwnerName: string
  bankAccountNumber: string
}

enum WalletStatus {
  Convert = 'convert',
  Create = 'create',
}

enum Field {
  ConfirmCheck = 'confirm_check',
  ComplyCheck = 'comply_check',
  PrivateBetaCheck = 'private_beta_check',
  BankAccount = 'bank_account',
}

enum BankAccountField {
  AccountNumber = 'bankAccountNumber',
  Name = 'bankAccountOwnerName',
}

type Props = {
  onStatusChange: (status: WalletConversionStatus) => void
}

const ConversionForm = ({ onStatusChange }: Props) => {
  const proTermsLinkValue = {
    'pro-terms-of-use-link': (chunks: Array<ReactNode>) => (
      <a
        href={BUSINESS_TERMS_OF_USE_URL}
        data-testid="pro-terms-of-use-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  }

  const proTermsAndConditionsLinkValue = {
    'pro-terms-and-conditions-link': (chunks: Array<ReactNode>) => (
      <a
        href={BUSINESS_TERMS_AND_CONDITIONS_URL}
        data-testid="pro-terms-and-conditions-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  }

  const privateBetaTermsLinkValue = {
    'private-beta-terms-of-use-link': (chunks: Array<ReactNode>) => (
      <a
        href={PRIVATE_BETA_TERMS_OF_USE_URL}
        data-testid="private-beta-terms-of-use-link"
        target="_blank"
        rel="noopener noreferrer"
      >
        {chunks}
      </a>
    ),
  }
  const formMethods = useForm<FormData>({
    mode: 'onChange',
  })

  const { register, handleSubmit, formState, getValues, setError } = formMethods
  const { errors, isSubmitting } = formState

  const { track } = useTracking()
  const translate = useTranslate('business.wallet')
  const getErrorMessage = useFormValidationMessage(errors, 'business.wallet.create.fields')
  const { relativeUrl } = useLocation()

  const { locale } = useIntl()
  const userId = useSession().user?.id
  const isDsaFlowEnabled = useFeatureSwitch('dsa_business_account_registration')
  const isProTermsAndConditionsFSEnabled = useFeatureSwitch('pro_terms_and_conditions_enabled')

  const { businessAddressesResponse, isLoading, error } = useFetchBusinessAddresses()

  const [balance, setBalance] = useState<WalletBalanceModel>()
  const [bankAccount, setBankAccount] = useState<BankAccountModel>()
  const [isConfirmationChecked, setIsConfirmationChecked] = useState(false)
  const [isComplianceChecked, setIsComplianceChecked] = useState(false)
  const [isPrivateBetaChecked, setIsPrivateBetaChecked] = useState(false)
  const [showConfirmError, setShowConfirmError] = useState(false)
  const [showComplyError, setShowComplyError] = useState(false)
  const [showPrivateBetaError, setShowPrivateBetaError] = useState(false)
  const [showBankAccountError, setShowBankAccountError] = useState(false)
  const [walletError, setWalletError] = useState<WalletConversionErrorModel>()
  const [isGeneralErrorShown, setIsGeneralErrorShown] = useState(false)

  const countriesWithHiddenBetaAgreement = [
    CountryId.ES,
    CountryId.NL,
    CountryId.FR,
    CountryId.IT,
    CountryId.BE,
    CountryId.LUX,
    CountryId.PT,
    CountryId.UK,
  ]

  // Private beta = users who has a business account which does not own a business address in France
  const isPrivateBeta =
    businessAddressesResponse?.addresses.businessAddress?.countryId && !error
      ? !countriesWithHiddenBetaAgreement.includes(
          businessAddressesResponse?.addresses.businessAddress?.countryId,
        )
      : false

  useEffect(() => {
    track(
      viewScreenEvent({
        screen: Screen.WalletConversion,
      }),
    )
  }, [track])

  useEffect(() => {
    async function fetchWalletBalance() {
      if (!userId) return

      const response = await getWalletBalance(userId)

      if ('errors' in response) return

      setBalance(transformWalletBalanceDto(response.user_balance))
    }

    fetchWalletBalance()
  }, [userId])

  useEffect(() => {
    async function fetchBankAccounts() {
      const response = await getBankAccounts()

      if ('errors' in response) return

      setBankAccount(
        transformBankAccounts(response.bank_accounts).find(account => account.isDefault),
      )
    }

    fetchBankAccounts()
  }, [])

  if (!balance) return null

  const totalAmount =
    parseFloat(balance.availableAmount.amount) + parseFloat(balance.pendingAmount.amount)
  const walletStatus = totalAmount > 0 ? WalletStatus.Convert : WalletStatus.Create
  const formattedAmount = formatCurrency(totalAmount, {
    locale,
    currency: balance.availableAmount.currencyCode,
  })

  const walletAccountUrl = urlWithParams(WALLET_ACCOUNT_EDIT_URL, {
    ref_url: relativeUrl,
    type: TwoFactorAuthentificationType.BankAccount,
  })

  function handleConfirmationChange() {
    setIsConfirmationChecked(prevIsConfirmationChecked => !prevIsConfirmationChecked)
    setShowConfirmError(false)
  }

  function handleComplianceChange() {
    setIsComplianceChecked(prevIsComplianceChecked => !prevIsComplianceChecked)
    setShowComplyError(false)
  }

  function handlePrivateBetaChange() {
    setIsPrivateBetaChecked(prevIsPrivateBetaChecked => !prevIsPrivateBetaChecked)
    setShowPrivateBetaError(false)
  }

  function handleConversionErrors(response: ResponseError<WalletConversionErrorDto>) {
    if (response.code === ResponseCode.ValidationError && response.payload?.title) {
      setWalletError(transformWalletConversionError(response.payload))
    }
  }

  const setBackendErrors = (fieldErrors: Array<ErrorItem>) => {
    fieldErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (isValueInObject(fieldName, BankAccountField)) {
        setError(fieldName, { type: 'manual', message: value })
      }
    })
  }

  async function handleFormSubmit() {
    let hasCheckboxError = false

    if (!isConfirmationChecked) {
      setShowConfirmError(true)
      hasCheckboxError = true
    }

    if (!isComplianceChecked && isDsaFlowEnabled) {
      setShowComplyError(true)
      hasCheckboxError = true
    }

    if (!isPrivateBetaChecked && isPrivateBeta) {
      setShowPrivateBetaError(true)
      hasCheckboxError = true
    }

    if (hasCheckboxError) {
      return
    }

    if (isDsaFlowEnabled && walletStatus === WalletStatus.Create) {
      const bankDetailsFields = getValues()

      if (!bankDetailsFields) return

      const result = await updateBusinessAccountBankDetails({
        bankAccountNumber: bankDetailsFields.bankAccountNumber,
        bankAccountOwnerName: bankDetailsFields.bankAccountOwnerName,
      })

      if ('errors' in result) {
        setBackendErrors(result.errors)

        if (
          result.status === HttpStatus.InternalError ||
          result.status === HttpStatus.Unauthorized
        ) {
          setIsGeneralErrorShown(true)

          return
        }

        return
      }
    }

    if (!bankAccount && walletStatus === WalletStatus.Convert) {
      setShowBankAccountError(true)

      return
    }

    const response = await convertBusinessWallet()

    if ('errors' in response) {
      handleConversionErrors(response)

      return
    }

    track(
      clickEvent({
        target: ClickableElement.CompleteRegistration,
        screen: Screen.WalletConversion,
      }),
    )

    if (response.state) {
      onStatusChange(response.state)
    }
  }

  function renderBankAccountBlock() {
    if (bankAccount) {
      return (
        <Cell
          title={bankAccount.name}
          body={bankAccount.accountNumber}
          url={walletAccountUrl}
          suffix={
            <Icon
              name={Pencil24}
              color={Icon.Color.GreyscaleLevel1}
              aria={{ 'aria-hidden': 'true' }}
            />
          }
          styling={Cell.Styling.Wide}
          type={Cell.Type.Navigating}
        />
      )
    }

    const validationMessage = showBankAccountError ? (
      <Validation theme="warning" text={translate(`errors.${Field.BankAccount}`)} />
    ) : undefined

    return (
      <Cell
        title={translate(`${walletStatus}.bank_account.empty`)}
        body={validationMessage}
        url={walletAccountUrl}
        suffix={
          <Icon name={Plus24} color={Icon.Color.GreyscaleLevel1} aria={{ 'aria-hidden': 'true' }} />
        }
        styling={Cell.Styling.Wide}
        type={Cell.Type.Navigating}
      />
    )
  }

  function renderConfirmCheckboxField() {
    const confirmCheckTranslation = isProTermsAndConditionsFSEnabled
      ? translate(`${Field.ConfirmCheck}_terms_and_conditions`, proTermsAndConditionsLinkValue)
      : translate(Field.ConfirmCheck)

    return (
      <>
        <Checkbox
          name={Field.ConfirmCheck}
          text={<Text as="span" html text={confirmCheckTranslation} />}
          checked={isConfirmationChecked}
          onChange={handleConfirmationChange}
        />
        {showConfirmError && (
          <>
            <Spacer size={Spacer.Size.Small} />
            <Validation theme="warning" text={translate(`errors.${Field.ConfirmCheck}`)} />
          </>
        )}
      </>
    )
  }

  function renderComplyCheckboxField() {
    return (
      <>
        <Checkbox
          name={Field.ComplyCheck}
          text={<Text as="span" html text={translate(Field.ComplyCheck, proTermsLinkValue)} />}
          checked={isComplianceChecked}
          onChange={handleComplianceChange}
        />
        {showComplyError && (
          <>
            <Spacer size={Spacer.Size.Small} />
            <Validation theme="warning" text={translate(`errors.${Field.ComplyCheck}`)} />
          </>
        )}
      </>
    )
  }

  function renderPrivateBetaCheckboxField() {
    return (
      <>
        <Checkbox
          name={Field.PrivateBetaCheck}
          text={
            <Text
              as="span"
              html
              text={translate(Field.PrivateBetaCheck, privateBetaTermsLinkValue)}
            />
          }
          checked={isPrivateBetaChecked}
          onChange={handlePrivateBetaChange}
        />
        {showPrivateBetaError && (
          <>
            <Spacer size={Spacer.Size.Small} />
            <Validation theme="warning" text={translate(`errors.${Field.PrivateBetaCheck}`)} />
          </>
        )}
      </>
    )
  }

  function renderCheckboxes() {
    if (isLoading)
      return (
        <>
          <Spacer size={Spacer.Size.X2Large} />
          <div
            data-testid="private-beta-checkbox-loader"
            className="u-flexbox u-justify-content-center u-align-items-center"
          >
            <Loader size={Loader.Size.XLarge} />
          </div>
          <Spacer size={Spacer.Size.X2Large} />
        </>
      )

    return (
      <>
        <Spacer size={Spacer.Size.X2Large} />
        {renderConfirmCheckboxField()}
        <Spacer size={Spacer.Size.X2Large} />
        {isPrivateBeta && (
          <>
            {renderPrivateBetaCheckboxField()}
            <Spacer size={Spacer.Size.X2Large} />
          </>
        )}
        {isDsaFlowEnabled && (
          <>
            {renderComplyCheckboxField()}
            <Spacer size={Spacer.Size.X2Large} />
          </>
        )}
      </>
    )
  }

  function handleModalClose() {
    setWalletError(undefined)
  }

  function handleSubmitClick(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) {
    if (isSubmitting) {
      event.preventDefault()

      return
    }

    handleFormSubmit()
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Text
          as="h1"
          type={Text.Type.Heading}
          text={translate(`${walletStatus}.title`, { amount: formattedAmount })}
        />
        <Spacer />
        <Text as="span">{translate(`${walletStatus}.subtitle`)}</Text>
        {walletStatus === WalletStatus.Convert && (
          <>
            <Spacer size={Spacer.Size.XLarge} />
            <Card>
              <div className="u-overflow-hidden">
                <Cell
                  title={translate(`${walletStatus}.withdrawal.title`)}
                  suffix={<Text as="span">{formattedAmount}</Text>}
                  styling={Cell.Styling.Wide}
                />
                <Divider />
                {renderBankAccountBlock()}
              </div>
            </Card>
          </>
        )}
        {isDsaFlowEnabled && walletStatus === WalletStatus.Create && (
          <>
            <Spacer size={Spacer.Size.XLarge} />
            {isGeneralErrorShown && <GeneralError />}
            <Spacer size={Spacer.Size.XLarge} />
            <Card>
              <SeparatedList separator={<Divider />}>
                <InputText
                  {...register(BankAccountField.Name, {
                    required: true,
                  })}
                  styling={InputText.Styling.Wide}
                  title={translate('create.fields.bank_account_owner_name.title')}
                  placeholder={translate('create.fields.bank_account_owner_name.placeholder')}
                  validation={renderValidation(getErrorMessage(BankAccountField.Name))}
                />
                <InputText
                  {...register(BankAccountField.AccountNumber, {
                    required: true,
                  })}
                  styling={InputText.Styling.Wide}
                  title={translate('create.fields.bank_account_number.title')}
                  placeholder={translate('create.fields.bank_account_number.placeholder')}
                  validation={renderValidation(getErrorMessage(BankAccountField.AccountNumber))}
                />
              </SeparatedList>
            </Card>
            <Spacer size={Spacer.Size.Large} />
          </>
        )}
        {renderCheckboxes()}
        <div className="u-flexbox u-justify-content-flex-end">
          <Button
            styling={Button.Styling.Filled}
            inline
            onClick={handleSubmitClick}
            isLoading={isSubmitting}
            text={translate('submit_button')}
          />
        </div>
        <ErrorModal error={walletError} onClose={handleModalClose} />
        <Spacer size={Spacer.Size.X4Large} />
      </form>
    </FormProvider>
  )
}

export default ConversionForm
